import { useState } from "react";
import { useLocation } from "wouter";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Folder, Truck, Package, PackagePlus, PackageMinus, Bell, CalendarCheck, Warehouse } from "lucide-react";
import { CategoriesTab } from "@/components/inventory/categories-tab";
import { VendorsTab } from "@/components/inventory/vendors-tab";
import { ItemsTab } from "@/components/inventory/items-tab";
import { StockInTab } from "@/components/operations/stock-in-tab";
import { StockOutTab } from "@/components/operations/stock-out-tab";
import { AlertsTab } from "@/components/operations/alerts-tab";
import { StockDayCloseTab } from "@/components/operations/stock-day-close-tab";
import { StockLocationsTab } from "@/components/operations/stock-locations-tab";

type TabValue = "categories" | "vendors" | "items" | "stock-in" | "stock-out" | "day-close" | "locations" | "alerts";

const TAB_VALUES: readonly TabValue[] = [
  "categories", "vendors", "items", "stock-in", "stock-out", "day-close", "locations", "alerts",
];

function tabFromPath(path: string): TabValue {
  // Match /inventory/operation/<tab>; fall back to "categories" on bare /inventory/operation.
  const suffix = path.replace(/^\/inventory\/operation\/?/, "").split("/")[0] ?? "";
  return (TAB_VALUES as readonly string[]).includes(suffix) ? (suffix as TabValue) : "categories";
}

const TAB_CTA: Record<TabValue, { label: string; icon: typeof PackagePlus } | null> = {
  "categories": { label: "Add Category", icon: Folder },
  "vendors":    { label: "Add Vendor", icon: Truck },
  "items":      { label: "Add Item", icon: Package },
  "stock-in":   { label: "Add Stock In", icon: PackagePlus },
  // Stock-Out has 4 mode-specific actions (General Consumption /
  // With-Limit / Pay-Per-Use / Internal Refills). A single page-level
  // "New Stock Out" button floating above the KPI strip is misleading
  // — the create CTA lives inside StockOutTab's main panel instead,
  // with a label that tracks the active sidebar item.
  "stock-out":  null,
  "day-close":  null,
  "locations":  null,
  "alerts":     null,
};

export default function InventoryOperation() {
  const [location, setLocation] = useLocation();
  const tab = tabFromPath(location);
  const [openCreate, setOpenCreate] = useState(false);
  const cta = TAB_CTA[tab];

  function handleTabChange(next: string) {
    if (next === tab) return;
    setOpenCreate(false);
    setLocation(`/inventory/operation/${next}`);
  }

  // Stock-In uses the d497aa6 "app-shell" pattern on POINTER-FINE
  // devices only (mouse-driven desktops + laptops). On touch devices
  // (Samsung S8, iPad, phones) it falls back to natural page scroll
  // so the table is reachable by swiping the page instead of being
  // trapped inside CardContent's internal scroll. Tailwind's
  // pointer-fine: variant is the cleanest signal here — it gates on
  // the device's primary pointer type, not screen size, so a S8
  // landscape (1380px wide, touch-only) correctly gets the natural
  // scroll path while a 1366×768 laptop still gets the app-shell.
  //
  // For Stock-In we layer BOTH layouts:
  //   * default: natural scroll, page header is sticky top-0
  //   * pointer-fine: app-shell, page header just shrink-0
  // Tailwind's pointer-fine: prefix overrides the default classes
  // where they conflict.
  const isStockIn = tab === "stock-in";
  return (
    <div className={
      isStockIn
        ? "max-w-screen-2xl mx-auto px-2 md:px-6 space-y-3 md:space-y-4 pointer-fine:h-full pointer-fine:flex pointer-fine:flex-col pointer-fine:space-y-0"
        : "max-w-screen-2xl mx-auto px-2 md:px-6 space-y-3 md:space-y-4"
    }>
      <Tabs
        value={tab}
        onValueChange={handleTabChange}
        className={isStockIn ? "pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:flex pointer-fine:flex-col" : undefined}
      >
        <div className={
          isStockIn
            ? "sticky top-0 z-30 -mx-2 md:-mx-6 px-2 md:px-6 pt-2 md:pt-4 pb-2 md:pb-3 bg-background space-y-3 md:space-y-4 pointer-fine:static pointer-fine:top-auto pointer-fine:z-auto pointer-fine:mx-0 pointer-fine:bg-transparent pointer-fine:shrink-0"
            : "sticky top-0 z-30 -mx-2 md:-mx-6 px-2 md:px-6 pt-2 md:pt-4 pb-2 md:pb-3 bg-background space-y-3 md:space-y-4"
        }>
          <header>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Inventory · Operation</h1>
            <p className="text-xs md:text-sm text-muted-foreground">Master data + daily ops — Categories, Vendors, Items, Stock-In, Stock-Out, Day Close, Locations, Alerts.</p>
          </header>

          {/* Mobile: horizontally scrollable strip. Desktop: even column grid (8 tabs). */}
          <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-8 h-auto md:h-10">
            <TabsTrigger value="categories" className="shrink-0 whitespace-nowrap"><Folder        className="mr-2 h-4 w-4" />Categories</TabsTrigger>
            <TabsTrigger value="vendors"    className="shrink-0 whitespace-nowrap"><Truck         className="mr-2 h-4 w-4" />Vendors</TabsTrigger>
            <TabsTrigger value="items"      className="shrink-0 whitespace-nowrap"><Package       className="mr-2 h-4 w-4" />Items</TabsTrigger>
            <TabsTrigger value="stock-in"   className="shrink-0 whitespace-nowrap"><PackagePlus   className="mr-2 h-4 w-4" />Stock-In</TabsTrigger>
            <TabsTrigger value="stock-out"  className="shrink-0 whitespace-nowrap"><PackageMinus  className="mr-2 h-4 w-4" />Stock-Out</TabsTrigger>
            <TabsTrigger value="day-close"  className="shrink-0 whitespace-nowrap"><CalendarCheck className="mr-2 h-4 w-4" />Day Close</TabsTrigger>
            <TabsTrigger value="locations"  className="shrink-0 whitespace-nowrap"><Warehouse     className="mr-2 h-4 w-4" />Locations</TabsTrigger>
            <TabsTrigger value="alerts"     className="shrink-0 whitespace-nowrap"><Bell          className="mr-2 h-4 w-4" />Alerts</TabsTrigger>
          </TabsList>

          {cta ? (
            <div className="flex justify-end">
              <Button size="sm" className="md:size-default" onClick={() => setOpenCreate(true)}>
                <cta.icon className="mr-2 h-4 w-4" />{cta.label}
              </Button>
            </div>
          ) : null}
        </div>
        <TabsContent value="categories"><CategoriesTab openCreate={tab === "categories" && openCreate} setOpenCreate={setOpenCreate} /></TabsContent>
        <TabsContent value="vendors"   ><VendorsTab    openCreate={tab === "vendors" && openCreate}    setOpenCreate={setOpenCreate} /></TabsContent>
        <TabsContent value="items"     ><ItemsTab      openCreate={tab === "items" && openCreate}      setOpenCreate={setOpenCreate} /></TabsContent>
        <TabsContent value="stock-in"  className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:flex pointer-fine:flex-col"><StockInTab    openCreate={tab === "stock-in" && openCreate}   setOpenCreate={setOpenCreate} /></TabsContent>
        <TabsContent value="stock-out" ><StockOutTab   openCreate={tab === "stock-out" && openCreate}  setOpenCreate={setOpenCreate} /></TabsContent>
        <TabsContent value="day-close" ><StockDayCloseTab /></TabsContent>
        <TabsContent value="locations" ><StockLocationsTab /></TabsContent>
        <TabsContent value="alerts"    ><AlertsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
